import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
} from "@mui/material";
import { useHistory, useLocation, Switch, Route } from "react-router-dom";
import { AccountBalanceWallet, Build, CheckCircle, DirectionsCar, Handyman, Inventory, Menu as MenuIcon, Person, School } from "@mui/icons-material";
import GroupIcon from "@mui/icons-material/Group";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { customIdPaths } from "../../utils/enums";
import { PATH_CUSTOM_IDS } from "../../utils/routeConstants";
import CustomIDGenerator from "../../global-components/CustomIDGenerator";
import { entityNamesForCustomId } from "../../utils/constants";

const drawerWidth = 240;
const collapsedDrawerWidth = 60;
const path = PATH_CUSTOM_IDS;

const dynamicMenuItems = [
  { label: "Employee ID", path: customIdPaths.EMPLOYEE_ID, icon: <Person />, isAMSId: null },
  { label: "Course ID", path: customIdPaths.COURSE_ID, icon: <School />, isAMSId: true },
  {
    label: "Certificate ID",
    path: customIdPaths.CERTIFICATE_ID,
    icon: <WorkspacePremiumIcon />,
    isAMSId: true,
  },
  {
    label: "Participant ID",
    path: customIdPaths.PARTICIPANT_ID,
    icon: <GroupIcon />,
    isAMSId: true,
  },
  {
    label: "Fleet ID",
    path: customIdPaths.FLEET_ID,
    icon: <DirectionsCar />,
    isAMSId: false,
  },
  {
    label: "Work Order ID",
    path: customIdPaths.WORKORDER_ID,
    icon: <Build />,
    isAMSId: false,
  },
  {
    label: "Inspection ID",
    path: customIdPaths.INSPECTION_ID,
    icon: <CheckCircle />,
    isAMSId: false,
  },
  {
    label: "Funding Source ID",
    path: customIdPaths.FUNDINGSOURCE_ID,
    icon: <AccountBalanceWallet />,
    isAMSId: false,
  },
  {
    label: "Equipment ID",
    path: customIdPaths.EQUIPMENT_ID,
    icon: <Handyman />,
    isAMSId: false,
  },
  {
    label: "Maintenance ID",
    path: customIdPaths.MAINTENANCE_ID,
    icon: <Build />,
    isAMSId: false,
  },
  {
    label: "Dispensable ID",
    path: customIdPaths.DISPENSABLE_ID,
    icon: <Inventory />,
    isAMSId: false,
  },
];

const CustomIds = () => {
  const history = useHistory();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(false);
  const userAgency = useSelector((state) => state.user.userAgency);

  const customIdRoutes = [
    {
      path: customIdPaths.EMPLOYEE_ID,
      initialData: {
        AgencyID: userAgency,
        Application: "AMS",
        EntityName: entityNamesForCustomId.personnelEmployeeId,
      },
      dataForNewItem: {
        Prefix: "",
        Suffix: "",
      },
      entityName: entityNamesForCustomId.personnelEmployeeId,
      displayName: "Personnel Employee ID",
    },
    {
      path: customIdPaths.COURSE_ID,
      initialData: {
        AgencyID: userAgency,
        Application: "AMS",
        EntityName: entityNamesForCustomId.CourseId,
      },
      dataForNewItem: {
        Prefix: "",
        Suffix: "",
      },
      entityName: entityNamesForCustomId.CourseId,
      displayName: "Course ID",
    },
    {
      path: customIdPaths.CERTIFICATE_ID,
      initialData: {
        AgencyID: userAgency,
        Application: "AMS",
        EntityName: entityNamesForCustomId.CertificateId,
      },
      dataForNewItem: {
        Prefix: "",
        Suffix: "",
      },
      entityName: entityNamesForCustomId.CertificateId,
      displayName: "Certificate ID",
    },
    {
      path: customIdPaths.PARTICIPANT_ID,
      initialData: {
        AgencyID: userAgency,
        Application: "AMS",
        EntityName: entityNamesForCustomId.ParticipantId,
      },
      dataForNewItem: {
        Prefix: "",
        Suffix: "",
      },
      entityName: entityNamesForCustomId.ParticipantId,
      displayName: "Participant ID",
    },
    {
      path: customIdPaths.FLEET_ID,
      initialData: {
        AgencyID: userAgency,
        Application: "AMS",
        EntityName: entityNamesForCustomId.FleetId,
      },
      dataForNewItem: {
        Prefix: "",
        Suffix: "",
      },
      entityName: entityNamesForCustomId.FleetId,
      displayName: "Fleet ID",
    },
    {
      path: customIdPaths.WORKORDER_ID,
      initialData: {
        AgencyID: userAgency,
        Application: "AMS",
        EntityName: entityNamesForCustomId.WorkOrderId,
      },
      dataForNewItem: {
        Prefix: "",
        Suffix: "",
      },
      entityName: entityNamesForCustomId.WorkOrderId,
      displayName: "Work Order ID",
    },
    {
      path: customIdPaths.INSPECTION_ID,
      initialData: {
        AgencyID: userAgency,
        Application: "AMS",
        EntityName: entityNamesForCustomId.InspectionId,
      },
      dataForNewItem: {
        Prefix: "",
        Suffix: "",
      },
      entityName: entityNamesForCustomId.InspectionId,
      displayName: "Inspection ID",
    },
    {
      path: customIdPaths.FUNDINGSOURCE_ID,
      initialData: {
        AgencyID: userAgency,
        Application: "AMS",
        EntityName: entityNamesForCustomId.FundingSourceId,
      },
      dataForNewItem: {
        Prefix: "",
        Suffix: "",
      },
      entityName: entityNamesForCustomId.FundingSourceId,
      displayName: "Funding Source ID",
    },
    {
      path: customIdPaths.EQUIPMENT_ID,
      initialData: {
        AgencyID: userAgency,
        Application: "AMS",
        EntityName: entityNamesForCustomId.EquipmentId,
      },
      dataForNewItem: {
        Prefix: "",
        Suffix: "",
      },
      entityName: entityNamesForCustomId.EquipmentId,
      displayName: "Equipment ID",
    },
    {
      path: customIdPaths.MAINTENANCE_ID,
      initialData: {
        AgencyID: userAgency,
        Application: "AMS",
        EntityName: entityNamesForCustomId.MaintenanceId,
      },
      dataForNewItem: {
        Prefix: "",
        Suffix: "",
      },
      entityName: entityNamesForCustomId.MaintenanceId,
      displayName: "Maintenance ID",
    },
    {
      path: customIdPaths.DISPENSABLE_ID,
      initialData: {
        AgencyID: userAgency,
        Application: "AMS",
        EntityName: entityNamesForCustomId.DispensableId,
      },
      dataForNewItem: {
        Prefix: "",
        Suffix: "",
      },
      entityName: entityNamesForCustomId.DispensableId,
      displayName: "Dispensable ID",
    }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerCollapse = () => {
    setIsDrawerCollapsed(!isDrawerCollapsed);
  };

  const drawerContent = (
    <Box sx={{ overflow: "auto" }}>
      <List>
        {dynamicMenuItems.map((item) => {
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => {
                  history.push(`${path}/${item.path}`);
                  if (mobileOpen) setMobileOpen(false);
                }}
                selected={location.pathname === `${path}/${item.path}`}
                sx={{
                  justifyContent: isDrawerCollapsed ? "center" : "initial",
                  px: isDrawerCollapsed ? 1 : 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isDrawerCollapsed ? "auto" : 56,
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isDrawerCollapsed && <ListItemText primary={item.label} />}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better performance on mobile
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          width: isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth,
            boxSizing: "border-box",
            transition: "width 0.2s",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton onClick={handleDrawerCollapse}>
            <MenuIcon />
          </IconButton>
        </Box>
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2 },
          width: {
            sm: `calc(100% - ${
              isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth
            }px)`,
          },
          transition: "width 0.2s",
        }}
      >
        <AppBar
          position="static"
          sx={{ display: { xs: "block", sm: "none" }, mb: 2 }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap>
              Custom IDs
            </Typography>
          </Toolbar>
        </AppBar>
        <Switch>
          {customIdRoutes.map((route) => (
            <Route
              key={route.path}
              path={`${path}/${route.path}`}
              render={() => (
                <CustomIDGenerator
                  initialData={route.initialData}
                  dataForNewItem={route.dataForNewItem}
                  entityName={route.entityName}
                  displayName={route.displayName}
                />
              )}
            />
          ))}
          <Route path={path}>
            <div></div>
          </Route>
        </Switch>
      </Box>
    </Box>
  );
};

export default CustomIds;
